if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.error = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get shopifyRoot() {
        return window.Shopify?.routes?.root || '/';
      }

      getCartSectionIds() {
        if (!this.cart?.getSectionsToRender) return ['cart-drawer', 'cart-icon-bubble'];
        return this.cart.getSectionsToRender().map((section) => section.id).filter(Boolean);
      }

      getSelectedUpsellItems(parentTitle) {
        return Array.from(this.querySelectorAll('[data-imed-upsell-check]:checked'))
          .map((input) => {
            const id = Number(input.value);
            if (!id) return null;

            const properties = {
              'Zu Reparatur': parentTitle || 'Reparaturauftrag',
            };

            const propKey = input.dataset.upsellPropertyKey;
            const propValue = input.dataset.upsellPropertyValue;
            if (propKey && propValue) {
              properties[propKey] = propValue;
            }

            const label = input.dataset.upsellLabel;
            if (label) {
              properties['_Upsell'] = label;
            }

            return { id, quantity: 1, properties };
          })
          .filter(Boolean);
      }

      async fetchSections(sectionIds) {
        const response = await fetch(`${this.shopifyRoot}?sections=${sectionIds.join(',')}`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Section refresh failed (${response.status})`);
        return response.json();
      }

      /**
       * Directly refresh the header cart icon (#cart-icon-bubble) from section + cart.js.
       * Independent of cart-drawer renderContents — this is what customers see top-right.
       */
      async refreshCartIconBubble() {
        const cartLink = document.getElementById('cart-icon-bubble');
        if (!cartLink) return;

        try {
          const [cart, sections] = await Promise.all([
            fetch(`${this.shopifyRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
            fetch(`${this.shopifyRoot}?sections=cart-icon-bubble`, {
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            }).then((r) => r.json()),
          ]);

          const sectionHtml = sections?.['cart-icon-bubble'];
          if (sectionHtml) {
            const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const source = doc.querySelector('.shopify-section') || doc.body;
            if (source) {
              cartLink.innerHTML = source.innerHTML;
            }
          }

          const count = Number(cart?.item_count) || 0;
          if (count > 0 && !cartLink.querySelector('.cart-count-bubble')) {
            const bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = `<span aria-hidden="true">${count < 100 ? count : ''}</span><span class="visually-hidden">${count}</span>`;
            cartLink.appendChild(bubble);
          }

          if (count > 0) {
            cartLink.classList.remove('header__icon--cart-empty');
          }
        } catch (error) {
          console.warn('refreshCartIconBubble failed', error);
        }
      }

      async addUpsells(upsellItems) {
        if (!upsellItems.length) return null;

        return fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: upsellItems }),
        }).then((r) => r.json());
      }

      paintCart(response) {
        if (!this.cart || !response) return;
        try {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        } catch (error) {
          console.warn('paintCart failed', error);
          // Still try to open the drawer if possible
          if (typeof this.cart.open === 'function') {
            this.cart.open();
          }
        }
      }

      resetSubmitButton() {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);
        const sectionIds = this.getCartSectionIds();
        const upsellRoot = this.querySelector('[data-imed-upsells]');
        const parentTitle = upsellRoot?.dataset.productTitle || '';
        const upsellItems = this.getSelectedUpsellItems(parentTitle);

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        // Main product — identical Dawn FormData + sections path (works without upsells)
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart && sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        try {
          let response = await fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

          if (response.status) {
            this.form.dataset.ffCheckout = '';
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
            return;
          }

          if (this.form.dataset.ffCheckout === '1') {
            if (upsellItems.length) {
              const upsellResponse = await this.addUpsells(upsellItems);
              if (upsellResponse?.status) {
                this.form.dataset.ffCheckout = '';
                this.handleErrorMessage(upsellResponse.description || 'Die Zusatzleistung konnte nicht hinzugefügt werden.');
                return;
              }
            }
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            window.location = root.replace(/\/?$/, '/') + 'checkout';
            return;
          }

          if (!this.cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          // Paint immediately — same moment as a simple add (bubble appears top-right)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
          this.paintCart(response);
          await this.refreshCartIconBubble();

          // Upsells after the UI already mirrors a normal add
          if (upsellItems.length) {
            const upsellResponse = await this.addUpsells(upsellItems);
            if (upsellResponse?.status) {
              console.warn('Upsell add failed', upsellResponse.description || upsellResponse.message);
            } else {
              try {
                const sections = await this.fetchSections(sectionIds);
                response = { ...response, sections };
                this.paintCart(response);
              } catch (sectionError) {
                console.warn('Post-upsell section refresh failed', sectionError);
              }
              await this.refreshCartIconBubble();
            }
          }
        } catch (e) {
          console.error(e);
          this.error = true;
          // Last resort: still sync the visible cart badge
          await this.refreshCartIconBubble();
        } finally {
          if (this.form.dataset.ffCheckout !== '1') {
            const pay = this.form.querySelector('[data-ff-pay]');
            if (pay) pay.disabled = false;
          }
          if (this.cart?.classList?.contains('is-empty')) this.cart.classList.remove('is-empty');
          // Never leave the ATC button permanently blocked after a cart add attempt
          if (!this.submitButton.querySelector('.sold-out-message:not([hidden])')) {
            this.resetSubmitButton();
          } else {
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          }
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      validateDiagnosisConsent() {
        // Checkbox entfernt – Hinweis ist rein informativ.
        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
